// src/components/EmployeeForm.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { api } from "../api";
import Select from "react-select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import * as XLSX from "xlsx";
import ChecklistPanelModal from "../Checklist/ChecklistPanelModal";
import ChecklistTasksCard from "../Checklist/ChecklistTasksCard";
import ChecklistFillModal from "../Checklist/ChecklistFillModal";
import "./EmployeeForm.css";

const theme = createTheme({
  palette: { primary: { main: "#673ab7" } },
});

const MAX_CHECKLIST_SIZE = 20 * 1024 * 1024; // 20 MB

const TABLE_EXTS = ["xlsx", "xls", "csv"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

const getChecklistKind = (fileName) => {
  const ext = String(fileName || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (TABLE_EXTS.includes(ext)) return "table";
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
};

const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const EmployeeForm = ({ user }) => {
  /* ---------------------- ROLES ---------------------- */
  const role = String(user?.role || "")
    .trim()
    .toLowerCase();
  const isAccountant = role === "accountant" || role === "accoutant";
  const isAccountantSVB = role === "accountantsvb";
  const isSales = role === "sales" || role === "sale";
  const isGraphicDesigner = role === "graphic designer";

  const restrictedCategoryForRole =
    isAccountant || isAccountantSVB
      ? "finance"
      : isSales
        ? "sales"
        : isGraphicDesigner
          ? "marketing"
          : null;

  /* ---------------------- CATEGORY NORMALIZATION FIX ---------------------- */

  // Make any category lowercase
  const catNorm = (c) =>
    String(c || "")
      .trim()
      .toLowerCase();

  // Make any category uppercase (like backend)
  const catUpper = (c) =>
    String(c || "")
      .trim()
      .toUpperCase();

  // Match backend uppercase category → closest frontend category label
  const findDisplayCategory = (backendCat) => {
    if (!backendCat) return "All";

    const up = catUpper(backendCat);

    // Available UI categories
    const uiCats = [
      "All",
      "USA",
      "CANADA",
      "Arch.ind",
      "Gruda",
      "DC",
      "ProjectData",
      "Interior",
      "BD & M",
      "Finance",
      "Sales",
      "Marketing",
    ];

    // Find matching label
    const found = uiCats.find((c) => catUpper(c) === up);

    if (found) return found;

    // Fallback formatting
    return up.charAt(0) + up.slice(1).toLowerCase();
  };

  /* ---------------------- INITIAL STATES ---------------------- */
  const [myPendingTasks, setMyPendingTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [completedBanner, setCompletedBanner] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    projectName: "",
    typeofProject: "",
    workType: "",
    drawingDocument: [],
    estimatedHours: "",
    remark: "",
    date: today, // ✅ Today ki date automatically
    startTime: "",
    endTime: "",
    version: "New",
  });

  const [projectsList, setProjectsList] = useState([]);
  const [drawingMaster, setDrawingMaster] = useState([]);
  const [logs, setLogs] = useState([]);
  const [actualHours, setActualHours] = useState({ minutes: 0, hours: "0.00" });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [editLogId, setEditLogId] = useState(null);

  /* ---------------------- CHECKLIST UPLOAD STATE ---------------------- */
  const [checklistFile, setChecklistFile] = useState(null);
  const [checklistRows, setChecklistRows] = useState([]);
  const [checklistPreviewUrl, setChecklistPreviewUrl] = useState("");
  const [checklistError, setChecklistError] = useState("");
  // true when the user picked a new file or removed the saved one (needs syncing to server)
  const [checklistDirty, setChecklistDirty] = useState(false);
  // name of the checklist already saved on the server for the log being edited
  const [savedChecklistName, setSavedChecklistName] = useState("");
  // checklist fill / assign popup
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  // bumps when the checklist popup closes, so the pending checklists card reloads
  const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);
  // filled checklist chosen to be attached to the timesheet entry
  const [attachChecklist, setAttachChecklist] = useState(null);
  // logId -> checklist id, for checklists already attached to a timesheet entry
  const [attachedByLog, setAttachedByLog] = useState({});
  // read-only popup used to view an attached checklist
  const [viewChecklistConfig, setViewChecklistConfig] = useState(null);
  const checklistInputRef = useRef(null);

  const checklistKind = checklistFile
    ? getChecklistKind(checklistFile.name)
    : "";

  // Create / clean up the preview URL for images and PDFs
  useEffect(() => {
    if (
      !checklistFile ||
      !["image", "pdf"].includes(getChecklistKind(checklistFile.name))
    ) {
      setChecklistPreviewUrl("");
      return;
    }

    const url = URL.createObjectURL(checklistFile);
    setChecklistPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [checklistFile]);

  const defaultNewProjectCategory =
    isAccountant || isAccountantSVB
      ? "Finance"
      : isSales
        ? "Sales"
        : isGraphicDesigner
          ? "Marketing"
          : "USA";

  const [category, setCategory] = useState(defaultNewProjectCategory);
  /* ---------------------- FILTERS / UI CATEGORIES ---------------------- */
  const baseCategories = [
    "All",
    "USA",
    "CANADA",
    "Arch.ind",
    "Gruda",
    "DC",
    "ProjectData",
    "Interior",
    "BD & M",
  ];

  const projectCategories =
    isAccountant || isAccountantSVB
      ? ["Finance"]
      : isSales
        ? ["Sales"]
        : isGraphicDesigner
          ? ["Marketing"]
          : baseCategories;

  const [projectTypeFilter, setProjectTypeFilter] = useState(
    isAccountant || isAccountantSVB
      ? "Finance"
      : isSales
        ? "Sales"
        : isGraphicDesigner
          ? "Marketing"
          : "All",
  );

  const [submitting, setSubmitting] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  function formatDateForInput(dateLike) {
    if (!dateLike) return "";
    try {
      const d = new Date(dateLike); // local timezone parse
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    } catch {
      return "";
    }
  }

  /* ---------------------- HELPERS ---------------------- */
  const computeEstimated = (base, version) => {
    const v = String(version || "").toLowerCase();
    const baseNum = Number(base) || 0;
    if (v === "r1") return (baseNum / 2).toFixed(2);
    if (v === "checking" || v === "cheking") return (baseNum / 4).toFixed(2);
    return baseNum.toFixed(2);
  };
  const formatEstimatedTime = (decimalHours) => {
    const totalMinutes = Math.round((Number(decimalHours) || 0) * 60);

    if (totalMinutes === 0) return "0 min";

    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} hr`;

    return `${hrs} hr ${mins} min`;
  };
  // allowCat now uses uppercase compare against projectTypeFilter
  const allowCat = (cat) => {
    const nLower = catNorm(cat);
    if (restrictedCategoryForRole) return nLower === restrictedCategoryForRole;
    if (["finance", "sales", "marketing"].includes(nLower)) return false;

    const filterUp = String(projectTypeFilter || "")
      .trim()
      .toUpperCase();
    if (!filterUp || filterUp === "ALL") return true;
    return (
      String(cat || "")
        .trim()
        .toUpperCase() === filterUp
    );
  };

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const pickErrMsg = (err) => {
    try {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const method = err?.config?.method?.toUpperCase();
      const code = err?.code;
      const respData = err?.response?.data;

      let body = "";
      if (typeof respData === "string") body = respData;
      else if (respData?.message) body = respData.message;
      else if (respData) body = JSON.stringify(respData);

      const parts = [
        status ? `HTTP ${status}` : null,
        method && url ? `${method} ${url}` : url ? url : null,
        code ? `code=${code}` : null,
        body ? `server says: ${body}` : null,
        !err?.response && err?.message ? err.message : null,
      ].filter(Boolean);

      return parts.length ? parts.join(" | ") : String(err || "Unknown error");
    } catch {
      return "Unknown error";
    }
  };

  /* ---------------------- CHECKLIST HANDLERS ---------------------- */
  const parseChecklistFile = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleChecklistUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setChecklistError("");

      if (file.size > MAX_CHECKLIST_SIZE) {
        setChecklistError("File is too large. Maximum size is 20 MB.");
        return;
      }

      const kind = getChecklistKind(file.name);
      let rows = [];

      if (kind === "table") {
        rows = await parseChecklistFile(file);
        if (!rows.length) {
          setChecklistError("The uploaded file is empty.");
          return;
        }
      }

      setChecklistFile(file);
      setChecklistRows(rows);
      setChecklistDirty(true);
    } catch (err) {
      console.warn("Checklist read failed", err);
      setChecklistError("Could not read this file. Please try another file.");
    } finally {
      // allow selecting the same file again
      e.target.value = "";
    }
  };

  const handleChecklistDownload = () => {
    if (!checklistFile) return;
    downloadBlob(checklistFile, checklistFile.name);
  };

  // User clicked "Remove" on the panel
  const handleChecklistRemove = () => {
    setChecklistFile(null);
    setChecklistRows([]);
    setChecklistError("");
    setChecklistDirty(true);
  };

  // Internal full reset (after submit / before loading another log)
  const resetChecklist = () => {
    setChecklistFile(null);
    setChecklistRows([]);
    setChecklistError("");
    setChecklistDirty(false);
    setSavedChecklistName("");
  };

  // Load the checklist already saved on the server (edit mode)
  const loadSavedChecklist = async (log) => {
    try {
      const res = await api.get(`/logs/checklist/${log._id}`, {
        ...authHeader,
        responseType: "blob",
      });
      const name = log.checklistFile?.originalName || "checklist";
      const file = new File([res.data], name, { type: res.data?.type || "" });

      let rows = [];
      if (getChecklistKind(name) === "table") {
        rows = await parseChecklistFile(file);
      }

      setChecklistFile(file);
      setChecklistRows(rows);
      setSavedChecklistName(name);
      setChecklistDirty(false);
    } catch (e) {
      console.warn("Failed to load saved checklist", e);
    }
  };

  // Download the saved checklist of a log straight from the logs table
  const handleLogChecklistDownload = async (log) => {
    try {
      const res = await api.get(`/logs/checklist/${log._id}`, {
        ...authHeader,
        responseType: "blob",
      });
      downloadBlob(res.data, log.checklistFile?.originalName || "checklist");
    } catch (err) {
      alert(`❌ Could not download checklist.\n${pickErrMsg(err)}`);
    }
  };

  // Save / replace / remove the checklist on the server for a saved log
  const syncChecklist = async (logId) => {
    if (!logId) {
      throw new Error(
        "Could not find the saved log id, so the checklist was not attached.",
      );
    }

    if (checklistFile) {
      const fd = new FormData();
      fd.append("checklist", checklistFile, checklistFile.name);
      await api.post(`/logs/checklist/${logId}`, fd, {
        headers: {
          ...authHeader.headers,
          "Content-Type": "multipart/form-data",
        },
      });
    } else if (editLogId && savedChecklistName) {
      await api.delete(`/logs/checklist/${logId}`, authHeader);
    }
  };

  /* ---------------------- FILLED CHECKLIST (ATTACH TO ENTRY) ---------------------- */

  // Checklist ticked in the "My Pending Checklists" card
  const handleSelectChecklist = (checklist) => {
    setAttachChecklist(checklist || null);

    if (checklist?.projectName) {
      setProjectTypeFilter("All");
      setForm((prev) => ({
        ...prev,
        projectName: checklist.projectName,
      }));
    }
  };

  // Checklists already attached to timesheet entries (for the logs table)
  const fetchAttachedChecklists = async () => {
    try {
      const res = await api.get("/checklists/attached", authHeader);
      const map = {};
      (Array.isArray(res.data) ? res.data : []).forEach((c) => {
        if (c.logId) map[String(c.logId)] = c._id;
      });
      setAttachedByLog(map);
    } catch (e) {
      console.warn("Failed to fetch attached checklists", e);
      setAttachedByLog({});
    }
  };

  // Open an attached checklist (read-only, comments are allowed)
  const handleViewAttachedChecklist = async (checklistId) => {
    try {
      const res = await api.get(`/checklists/${checklistId}`, authHeader);
      const doc = res.data || {};
      const htmlPath = doc.templateId?.htmlPath;

      if (!htmlPath) {
        alert("This checklist template is not available.");
        return;
      }

      setViewChecklistConfig({
        mode: "assignment",
        title: doc.templateTitle,
        htmlPath,
        assignmentId: doc._id,
        projectName: doc.projectName,
        data: doc.data || {},
        readOnly: true,
        defaults: {},
        users: [],
      });
    } catch (err) {
      alert(`❌ Could not open the checklist.\n${pickErrMsg(err)}`);
    }
  };

  /* ---------------------- API FETCHES ---------------------- */
  const fetchMyTasks = async () => {
    try {
      const res = await api.get("/tasks/my?status=pending", authHeader);
      setMyPendingTasks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn("Failed to fetch tasks", e);
      setMyPendingTasks([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get("/projects", authHeader);
      const all = Array.isArray(res.data) ? res.data : [];
      setProjectsList(
        all.filter((p) => {
          const n = catNorm(p.category);
          if (restrictedCategoryForRole) return n === restrictedCategoryForRole;
          return !["finance", "sales", "marketing"].includes(n);
        }),
      );
    } catch (e) {
      console.warn("Failed to fetch projects", e);
      setProjectsList([]);
    }
  };

  const fetchDrawingMaster = async () => {
    try {
      const res = await api.get("/drawing-master", authHeader);
      setDrawingMaster(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn("Failed to fetch drawing master", e);
      setDrawingMaster([]);
    }
  };

  const fetchLogs = async (forceSelectMonth = false) => {
    try {
      const res = await api.get("/logs/my", authHeader);
      const all = Array.isArray(res.data) ? res.data : [];

      const filtered = all.filter((l) => {
        const n = catNorm(l.projectCategory);
        if (restrictedCategoryForRole) return n === restrictedCategoryForRole;
        return !["finance", "sales", "marketing"].includes(n);
      });

      setLogs(filtered);

      const months = [
        ...new Set(
          filtered.map((log) => {
            const d = new Date(log.date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
              2,
              "0",
            )}`;
          }),
        ),
      ]
        .sort()
        .reverse();

      if (months.length) {
        if (
          forceSelectMonth ||
          !selectedMonth ||
          !months.includes(selectedMonth)
        ) {
          setSelectedMonth(months[0]);
        }
      } else {
        setSelectedMonth("");
      }
      return filtered;
    } catch (e) {
      console.warn("Failed to fetch logs", e);
      setLogs([]);
      setSelectedMonth("");
      return [];
    }
  };

  /* ---------------------- completion banner auto-clear ---------------------- */
  useEffect(() => {
    if (!completedBanner) return;
    const t = setTimeout(() => setCompletedBanner(null), 6000);
    return () => clearTimeout(t);
  }, [completedBanner]);

  /* ---------------------- initial load ---------------------- */
  useEffect(() => {
    fetchProjects();
    fetchDrawingMaster();
    fetchLogs(true);
    fetchMyTasks();
    fetchAttachedChecklists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* ---------------------- role filter auto-correct ---------------------- */
  useEffect(() => {
    if ((isAccountant || isAccountantSVB) && projectTypeFilter !== "Finance")
      setProjectTypeFilter("Finance");
    if (isSales && projectTypeFilter !== "Sales") setProjectTypeFilter("Sales");
    if (isGraphicDesigner && projectTypeFilter !== "Marketing")
      setProjectTypeFilter("Marketing");

    if (
      !isAccountant &&
      !isAccountantSVB &&
      !isSales &&
      !isGraphicDesigner &&
      projectTypeFilter !== "All"
    ) {
      if (["Finance", "Sales", "Marketing"].includes(projectTypeFilter))
        setProjectTypeFilter("All");
    }
  }, [
    isAccountant,
    isAccountantSVB,
    isSales,
    isGraphicDesigner,
    projectTypeFilter,
  ]);

  /* ---------------------- actual hours calc ---------------------- */
  useEffect(() => {
    const { startTime, endTime } = form;

    if (startTime && endTime) {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);

      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      const duration = endMinutes - startMinutes;

      setActualHours({
        minutes: duration > 0 ? duration : 0,
        hours: duration > 0 ? (duration / 60).toFixed(2) : "0.00",
      });
    } else {
      setActualHours({ minutes: 0, hours: "0.00" });
    }
  }, [form.startTime, form.endTime]);

  /* ---------------------- sanitize accidental restricted docs ---------------------- */
  useEffect(() => {
    if (Array.isArray(form.drawingDocument) && form.drawingDocument.length) {
      const allowed = form.drawingDocument.filter((doc) => {
        const item = drawingMaster.find((d) => d.drawingDocument === doc);
        return item && allowCat(item.category);
      });

      if (allowed.length !== form.drawingDocument.length) {
        setForm((prev) => ({ ...prev, drawingDocument: allowed }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAccountant,
    isAccountantSVB,
    isSales,
    isGraphicDesigner,
    projectTypeFilter,
    drawingMaster,
  ]);

  /* ---------------------- last used typeofProject per project ---------------------- */
  const getLogTimestamp = (log) => {
    try {
      if (log?.date) {
        const dateStr = new Date(log.date).toISOString().slice(0, 10);
        const t =
          (log?.endTime && `${dateStr}T${log.endTime}:00`) ||
          (log?.startTime && `${dateStr}T${log.startTime}:00`) ||
          `${dateStr}T00:00:00`;
        return new Date(t).getTime();
      }
      if (log?.createdAt) return new Date(log.createdAt).getTime();
    } catch {}
    return 0;
  };

  const lastTypeByProject = useMemo(() => {
    const map = Object.create(null);
    const bestTs = Object.create(null);

    for (const lg of logs) {
      const proj = lg?.projectName?.trim();
      const type = lg?.typeofProject?.trim();
      if (!proj || !type) continue;

      const ts = getLogTimestamp(lg);

      if (bestTs[proj] === undefined || ts > bestTs[proj]) {
        bestTs[proj] = ts;
        map[proj] = type;
      }
    }
    return map;
  }, [logs]);

  /* ---------------------- auto-fill typeofProject when project changes ---------------------- */
  const prevProjectRef = useRef("");

  useEffect(() => {
    const proj = form.projectName?.trim();
    if (!proj) return;

    if (proj !== prevProjectRef.current) {
      const lastType = lastTypeByProject[proj];

      if (lastType) {
        setForm((prev) => ({
          ...prev,
          typeofProject:
            prev.typeofProject && prev.typeofProject.trim()
              ? prev.typeofProject
              : lastType,
        }));
      }
      prevProjectRef.current = proj;
    }
  }, [form.projectName, lastTypeByProject]);

  /* ---------------------- select fields ---------------------- */
  const handleSelect = (field, value) => {
    setForm((prev) => {
      const updatedForm = { ...prev, [field]: value };

      if (field === "projectName") {
        const proj = (value || "").trim();
        const lastType = lastTypeByProject[proj];

        if (lastType && !prev.typeofProject) {
          updatedForm.typeofProject = lastType;
        }
      }

      if (field === "drawingDocument") {
        const selected = Array.isArray(value) ? value : [];
        const baseTotal = selected.reduce((sum, name) => {
          const match = drawingMaster.find(
            (d) => d.drawingDocument === name && allowCat(d.category),
          );
          const t = parseFloat(match?.estimatedHours || 0);
          return sum + (Number.isFinite(t) ? t : 0);
        }, 0);

        updatedForm.estimatedHours =
          selected.length > 0
            ? computeEstimated(baseTotal, updatedForm.version)
            : "";
      }

      return updatedForm;
    });
  };

  const handleVersionChange = (version) =>
    setForm((prev) => {
      const selected = Array.isArray(prev.drawingDocument)
        ? prev.drawingDocument
        : [];

      const baseTotal = selected.reduce((sum, name) => {
        const match = drawingMaster.find(
          (d) => d.drawingDocument === name && allowCat(d.category),
        );
        const t = parseFloat(match?.estimatedHours || 0);
        return sum + (Number.isFinite(t) ? t : 0);
      }, 0);

      const updatedTime =
        selected.length > 0 ? computeEstimated(baseTotal, version) : "";

      return { ...prev, version, estimatedHours: updatedTime };
    });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  /* ---------------------- validate form ---------------------- */
  const validateBeforeSubmit = () => {
    const messages = [];

    if (!form.projectName.trim()) messages.push("- Project Name");
    if (!form.typeofProject.trim()) messages.push("- Type of Project");
    if (
      !Array.isArray(form.drawingDocument) ||
      form.drawingDocument.length === 0
    )
      messages.push("- Drawing/Document (at least one)");
    if (!form.version.trim()) messages.push("- Version");

    if (form.startTime && form.endTime) {
      const [sh, sm] = form.startTime.split(":").map(Number);
      const [eh, em] = form.endTime.split(":").map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;

      if (endMinutes <= startMinutes) {
        messages.push("- End Time must be after Start Time");
      }
    }

    if (
      attachChecklist &&
      String(attachChecklist.projectName || "").trim() !==
        String(form.projectName || "").trim()
    ) {
      messages.push(
        `- The selected checklist is for project "${attachChecklist.projectName}". Select the same project or untick the checklist.`,
      );
    }

    if (messages.length) {
      alert("❌ Please fill/ fix:\n" + messages.join("\n"));
      return false;
    }

    return true;
  };

  /* ---------------------- Recompute estimate when docs or version change ---------------------- */
  useEffect(() => {
    if (editLogId) return; // ✅ Edit mode me estimate overwrite mat karo

    const selected = Array.isArray(form.drawingDocument)
      ? form.drawingDocument
      : [];

    const baseTotal = selected.reduce((sum, name) => {
      const match = drawingMaster.find((d) => d.drawingDocument === name);

      return sum + Number(match?.estimatedHours || 0);
    }, 0);

    const next =
      selected.length > 0 ? computeEstimated(baseTotal, form.version) : "";

    setForm((prev) =>
      prev.estimatedHours === next ? prev : { ...prev, estimatedHours: next },
    );
  }, [form.drawingDocument, form.version, drawingMaster, editLogId]);

  // isEdit = true -> same card, but marked as an updated entry
  // checklistInfo = { logId, name } when the entry has an uploaded checklist file
  // filledChecklist = the filled checklist (ERP checklist page) attached to this entry
  const postEmployeeLogToProjectChat = async (
    savedLog,
    matchedProject,
    isEdit = false,
    checklistInfo = null,
    filledChecklist = null,
  ) => {
    if (!savedLog || !matchedProject?._id) return;

    const remarkText = form.remark || "-";

    const content = [
      "Employee Entry:",
      `Project: ${form.projectName}`,
      `Type: ${form.typeofProject}`,
      `Work: ${form.workType || "-"}`,
      `Drawing: ${(form.drawingDocument || []).join(" | ")}`,
      `Version: ${form.version}`,
      `Estimated Hours: ${form.estimatedHours || 0}`,
      `Actual Hours: ${actualHours.hours || 0}`,
      `Date: ${form.date || new Date().toISOString().slice(0, 10)}`,
      `Time: ${form.startTime || "-"} to ${form.endTime || "-"}`,
      ...(checklistInfo?.logId
        ? [
            `Checklist: ${checklistInfo.name}`,
            `ChecklistLogId: ${checklistInfo.logId}`,
          ]
        : []),
      ...(filledChecklist?._id
        ? [
            `FilledChecklist: ${filledChecklist.templateTitle}`,
            `FilledChecklistId: ${filledChecklist._id}`,
          ]
        : []),
      isEdit
        ? `Remark: ✏️ UPDATED ENTRY${remarkText !== "-" ? ` - ${remarkText}` : ""}`
        : `Remark: ${remarkText}`,
    ].join("\n");

    await api.post(
      `/project-messages/${matchedProject._id}/messages`,
      {
        type: "employee-log",
        content,
      },
      authHeader,
    );
  };
  /* ---------------------- SUBMIT / SAVE LOG ---------------------- */
  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) return;

    const roundedActual = Number(actualHours.hours || 0);
    const roundedEstimated = Number(form.estimatedHours || 0);

    const matchedProject = projectsList.find(
      (p) => p.name === form.projectName,
    );
    const matchedCategory = (matchedProject?.category || "").trim();
    const n = catNorm(matchedCategory);

    if (
      !restrictedCategoryForRole &&
      ["finance", "sales", "marketing"].includes(n)
    ) {
      return alert(
        "You are not allowed to submit entries for restricted categories.",
      );
    }

    if (restrictedCategoryForRole && n !== restrictedCategoryForRole) {
      const label =
        restrictedCategoryForRole === "finance"
          ? "Finance"
          : restrictedCategoryForRole === "sales"
            ? "Sales"
            : "Marketing";
      return alert(`${label} role can submit only ${label} category entries.`);
    }

    const dateISO = form.date
      ? new Date(form.date + "T00:00:00").toISOString()
      : new Date().toISOString();

    const data = {
      projectName: form.projectName,
      typeofProject: form.typeofProject,
      workType: form.workType,
      drawingDocument: (form.drawingDocument || []).join(" | "),
      estimatedHours: Number.isFinite(roundedEstimated)
        ? Number(roundedEstimated.toFixed(2))
        : 0,
      actualHours: Number.isFinite(roundedActual)
        ? Number(roundedActual.toFixed(2))
        : 0,
      remark: form.remark,
      date: dateISO,
      startTime: form.startTime,
      endTime: form.endTime,
      version: form.version,
    };

    try {
      setSubmitting(true);
      let savedLog = null;

      if (editLogId) {
        const res = await api.put(`/logs/update/${editLogId}`, data, {
          headers: {
            ...authHeader.headers,
            "Content-Type": "application/json",
          },
        });
        alert("Log Updated ✅");
        // some backends return only a message on update -> keep the edited id
        savedLog = res?.data?._id
          ? res.data
          : { ...(res?.data || {}), _id: editLogId };
      } else {
        const res = await api.post("/logs/add", data, {
          headers: {
            ...authHeader.headers,
            "Content-Type": "application/json",
          },
        });
        alert("Log Saved ✅");
        savedLog = res?.data || null;
      }

      // the id may come back directly or wrapped inside log / data
      const savedLogId =
        savedLog?._id ||
        savedLog?.log?._id ||
        savedLog?.data?._id ||
        savedLog?.drawingLog?._id ||
        editLogId ||
        null;

      // true when this entry has a checklist saved on the server
      let checklistSavedOk = false;

      // save / replace / remove the checklist file for this log
      if (checklistDirty) {
        try {
          await syncChecklist(savedLogId);
          checklistSavedOk = Boolean(checklistFile);
        } catch (checkErr) {
          console.warn(
            "Checklist save failed:",
            checkErr?.message,
            checkErr?.response?.status,
            checkErr?.response?.data,
            checkErr,
          );
          alert(
            `⚠️ Log saved, but the checklist could not be saved.\n${pickErrMsg(checkErr)}`,
          );
        }
      } else if (checklistFile && savedChecklistName) {
        // editing an entry that already has a saved checklist (unchanged)
        checklistSavedOk = true;
      }

      // attach the filled checklist (chosen in "My Pending Checklists") to this entry
      let attachedChecklist = null;
      if (attachChecklist?._id && savedLogId) {
        try {
          await api.patch(
            `/checklists/${attachChecklist._id}/attach-log`,
            { logId: savedLogId },
            authHeader,
          );
          attachedChecklist = attachChecklist;
        } catch (attachErr) {
          console.warn("Checklist attach failed:", attachErr);
          alert(
            `⚠️ Log saved, but the checklist could not be attached.\n${pickErrMsg(attachErr)}`,
          );
        }
      }

      const checklistInfo =
        checklistSavedOk && savedLogId
          ? { logId: savedLogId, name: checklistFile.name }
          : null;

      // post to project chat for BOTH new entry and edited entry
      try {
        await postEmployeeLogToProjectChat(
          savedLog,
          matchedProject,
          Boolean(editLogId),
          checklistInfo,
          attachedChecklist,
        );
      } catch (chatErr) {
        console.warn("Employee entry chat post failed:", chatErr);
      }

      /* ---------------------- COMPLETE TASKS HANDLING ---------------------- */

      let didComplete = false;

      if (selectedTaskId) {
        try {
          const res = await api.patch(
            `/tasks/${selectedTaskId}/complete`,
            { completedLogId: savedLog?._id || null },
            authHeader,
          );

          const t = res?.data;
          if (t?._id) {
            setCompletedBanner({
              taskId: t._id,
              projectName: t.projectName,
              typeofProject: t.typeofProject,
              workType: t.workType,
              docs: t.drawingDocuments || [],
            });

            setMyPendingTasks((prev) =>
              prev.filter((x) => String(x._id) !== String(t._id)),
            );

            setSelectedTaskId(null);
            didComplete = true;
          }
        } catch (e) {
          console.warn("Direct task complete failed:", e);
        }
      }

      if (!didComplete) {
        try {
          const body = {
            projectName: form.projectName,
            typeofProject: form.typeofProject || undefined,
            workType: form.workType || undefined,
            drawingDocuments: Array.isArray(form.drawingDocument)
              ? form.drawingDocument
              : [],
            logId: savedLog?._id || null,
          };

          const completeRes = await api.post(
            "/tasks/complete-by-match",
            body,
            authHeader,
          );
          const { completed, task } = completeRes?.data || {};

          if (completed && task?._id) {
            setCompletedBanner({
              taskId: task._id,
              projectName: task.projectName,
              typeofProject: task.typeofProject,
              workType: task.workType,
              docs: task.drawingDocuments || [],
            });

            setMyPendingTasks((prev) =>
              prev.filter((t) => String(t._id) !== String(task._id)),
            );

            if (selectedTaskId === task._id) setSelectedTaskId(null);
          }
        } catch (autoErr) {
          console.warn("Auto task complete failed:", autoErr);
        }
      }

      /* ---------------------- REFRESH state ---------------------- */
      await fetchLogs(true);
      await fetchMyTasks();
      await fetchAttachedChecklists();
      setChecklistRefreshKey((k) => k + 1);

      /* ---------------------- RESET FORM ---------------------- */
      setForm({
        projectName: "",
        typeofProject: "",
        workType: "",
        drawingDocument: [],
        estimatedHours: "",
        remark: "",
        date: new Date().toISOString().split("T")[0],
        startTime: "",
        endTime: "",
        version: "New",
      });

      setActualHours({ minutes: 0, hours: "0.00" });
      setEditLogId(null);
      setSelectedTaskId(null);
      setAttachChecklist(null);
      resetChecklist();
    } catch (err) {
      const msg = pickErrMsg(err);
      alert(`❌ Failed to save.\n${msg}`);
    } finally {
      setSubmitting(false);
    }
  };
  /* ---------------------- EDIT LOG ---------------------- */
  const handleEdit = (log) => {
    const project = projectsList.find((p) => p.name === log.projectName);

    const projCat =
      (project?.category || "").trim() || (log.projectCategory || "").trim();

    const n = catNorm(projCat);

    if (
      !restrictedCategoryForRole &&
      ["finance", "sales", "marketing"].includes(n)
    )
      return alert("You are not allowed to edit restricted category entries.");

    if (restrictedCategoryForRole && n !== restrictedCategoryForRole) {
      const label =
        restrictedCategoryForRole === "finance"
          ? "Finance"
          : restrictedCategoryForRole === "sales"
            ? "Sales"
            : "Marketing";
      return alert(`${label} role can edit only ${label} entries.`);
    }

    // FIX — category of this log always shown in dropdown
    const safeDisplayCategory = findDisplayCategory(projCat);
    setProjectTypeFilter(safeDisplayCategory);

    // DOCS ARRAY
    const docsArray = Array.isArray(log.drawingDocument)
      ? log.drawingDocument
      : String(log.drawingDocument || "")
          .split(/\s*\|\s*|,\s*/g)
          .filter(Boolean);

    // Recompute estimate
    const normVersion =
      log.version === "Cheking" ? "Checking" : log.version || "New";

    // Don't filter by category while editing
    const baseTotal = docsArray.reduce((sum, name) => {
      const match = drawingMaster.find((d) => d.drawingDocument === name);

      const t = Number(match?.estimatedHours || 0);
      return sum + t;
    }, 0);

    // Use saved estimate if drawing master not found
    const recomputed =
      Number(log.estimatedHours) > 0
        ? log.estimatedHours
        : computeEstimated(baseTotal, normVersion);

    setForm({
      projectName: log.projectName || "",
      typeofProject: log.typeofProject || "",
      workType: log.workType || "",
      drawingDocument: docsArray,
      estimatedHours: recomputed,
      remark: log.remark || "",
      date: formatDateForInput(log.date),
      startTime: log.startTime || "",
      endTime: log.endTime || "",
      version: normVersion,
    });

    setEditLogId(log._id);

    // load the checklist saved with this log (if any)
    resetChecklist();
    if (log.checklistFile?.originalName) {
      loadSavedChecklist(log);
    }
  };

  /* ---------------------- DELETE LOG ---------------------- */
  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure?")) return;
    await api.delete(`/logs/${logId}`, authHeader);
    alert("Deleted ✅");
    await fetchLogs();
  };

  /* ---------------------- RECENT BUTTON HELPERS ---------------------- */
  const recentDrawings = [...new Set(logs.map((l) => l.workType))]
    .slice(-3)
    .reverse();

  const recentDrawingDocumentList = [
    ...new Set(
      logs.flatMap((l) =>
        Array.isArray(l.drawingDocuments) && l.drawingDocuments.length
          ? l.drawingDocuments
          : String(l.drawingDocument || "")
              .split(/\s*\|\s*|,\s*/g)
              .filter(Boolean),
      ),
    ),
  ]
    .slice(-3)
    .reverse();

  /* ---------------------- MONTH LIST ---------------------- */
  const uniqueMonthYears = [
    ...new Set(
      logs.map((log) => {
        const d = new Date(log.date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0",
        )}`;
      }),
    ),
  ]
    .sort()
    .reverse();

  useEffect(() => {
    if (uniqueMonthYears.length > 0 && !selectedMonth) {
      setSelectedMonth(uniqueMonthYears[0]);
    }
  }, [uniqueMonthYears, selectedMonth]);

  const logsThisMonth = logs.filter((log) => {
    const d = new Date(log.date);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
    return selectedMonth ? monthStr === selectedMonth : true;
  });

  const totalEstimated = logsThisMonth.reduce(
    (sum, log) => sum + (parseFloat(log.estimatedHours) || 0),
    0,
  );
  const totalActual = logsThisMonth.reduce(
    (sum, log) => sum + (parseFloat(log.actualHours) || 0),
    0,
  );

  /* ---------------------- Project-wise Summary ---------------------- */
  const projectHours = {};
  logsThisMonth.forEach((log) => {
    const key = log.projectName || "Unnamed";
    if (!projectHours[key]) projectHours[key] = { estimated: 0, actual: 0 };
    projectHours[key].estimated += parseFloat(log.estimatedHours || 0);
    projectHours[key].actual += parseFloat(log.actualHours || 0);
  });

  /* ---------------------- Type options ---------------------- */
  const typeOptions = drawingMaster
    .filter((d) => allowCat(d.category))
    .map((d) => d.typeofProject)
    .filter(
      (val, i, arr) => val && val.trim() !== "" && arr.indexOf(val) === i,
    );

  if (form.typeofProject && !typeOptions.includes(form.typeofProject)) {
    typeOptions.push(form.typeofProject);
  }

  const selectTypeOptions = typeOptions.map((val) => ({
    label: val,
    value: val,
  }));

  /* ---------------------- Filtered projects with uppercase FIX ---------------------- */
  const filteredProjects = projectsList.filter((p) => {
    if (projectTypeFilter === "All") return true;

    const catBackend = String(p.category || "")
      .trim()
      .toUpperCase();
    const filterUp = String(projectTypeFilter || "")
      .trim()
      .toUpperCase();

    return catBackend === filterUp;
  });

  const getMonthLabel = (val) => {
    const [year, month] = val.split("-");
    const date = new Date(`${year}-${month}-01`);
    return date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };
  /* ---------------------- RENDER JSX ---------------------- */
  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 24,
          padding: 16,
          flexWrap: "wrap",
        }}
      >
        {/* LEFT: Entry form */}
        <div
          className="card"
          style={{ flex: "999 1 560px", minWidth: 280, maxWidth: 980 }}
        >
          <h2
            style={{ textAlign: "center", color: "#343A76", marginBottom: 16 }}
          >
            Drawing/Document entry
          </h2>

          {/* Project Category Filter */}
          <div style={{ margin: "16px auto", width: "100%", maxWidth: 880 }}>
            <label
              style={{ fontWeight: "bold", display: "block", marginBottom: 8 }}
            >
              Filter by Project Category
            </label>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {projectCategories.map((cat) => (
                <label
                  key={cat}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <input
                    type="radio"
                    name="categoryFilter"
                    value={cat}
                    checked={projectTypeFilter === cat}
                    onChange={() => setProjectTypeFilter(cat)}
                    disabled={
                      isAccountant ||
                      isAccountantSVB ||
                      isSales ||
                      isGraphicDesigner
                    }
                  />
                  {cat}
                </label>
              ))}
            </div>
          </div>

          {/* Project Name + Type of Project */}
          <div
            style={{
              border: "1px solid #0f8a0f",
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <label
                style={{ minWidth: 130, fontWeight: "bold", fontSize: 14 }}
              >
                Project Name :
              </label>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Select
                  options={filteredProjects.map((p) => ({
                    label: p.name,
                    value: p.name,
                  }))}
                  value={
                    form.projectName
                      ? { label: form.projectName, value: form.projectName }
                      : null
                  }
                  onChange={(opt) =>
                    handleSelect("projectName", opt?.value || "")
                  }
                  placeholder="Select or type project..."
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <label
                style={{ minWidth: 130, fontWeight: "bold", fontSize: 14 }}
              >
                Type of Project :
              </label>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Select
                  options={selectTypeOptions}
                  value={
                    form.typeofProject
                      ? { label: form.typeofProject, value: form.typeofProject }
                      : null
                  }
                  onChange={(opt) =>
                    handleSelect("typeofProject", opt?.value || "")
                  }
                  placeholder="Select or type type..."
                />
              </div>
            </div>
          </div>

          {/* Work Type + Drawing & Document */}
          <div
            style={{
              border: "1px solid #00008B",
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <label
              style={{ fontWeight: "bold", marginBottom: 8, display: "block" }}
            >
              Work Type:
            </label>
            <Select
              options={[
                ...new Set(
                  drawingMaster
                    .filter((d) => allowCat(d.category))
                    .map((d) => d.workType),
                ),
              ].map((d) => ({ label: d, value: d }))}
              value={
                form.workType
                  ? { label: form.workType, value: form.workType }
                  : null
              }
              onChange={(opt) => handleSelect("workType", opt?.value || "")}
              placeholder="Select or type work type..."
            />

            {recentDrawings.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {recentDrawings.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelect("workType", item)}
                    style={{
                      fontSize: 12,
                      color: "#343A76",
                      background: "#eef2ff",
                      border: "1px solid #ccc",
                      padding: "4px 8px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}

            <label
              style={{
                fontWeight: "bold",
                marginTop: 12,
                display: "block",
                marginBottom: 8,
              }}
            >
              Drawing / Document Type:
            </label>
            <Select
              isMulti
              options={[
                ...new Set(
                  drawingMaster
                    .filter((d) => allowCat(d.category))
                    .map((d) => d.drawingDocument),
                ),
              ].map((s) => ({ label: s, value: s }))}
              value={(Array.isArray(form.drawingDocument)
                ? form.drawingDocument
                : []
              ).map((s) => ({
                label: s,
                value: s,
              }))}
              onChange={(opts) =>
                handleSelect(
                  "drawingDocument",
                  (opts || []).map((o) => o.value),
                )
              }
              placeholder="Choose drawings or documents..."
            />

            {recentDrawingDocumentList.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {recentDrawingDocumentList.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      handleSelect(
                        "drawingDocument",
                        Array.from(
                          new Set([...(form.drawingDocument || []), item]),
                        ),
                      )
                    }
                    style={{
                      fontSize: 12,
                      color: "#343A76",
                      background: "#eef2ff",
                      border: "1px solid #ccc",
                      padding: "4px 8px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}

            <label
              style={{
                fontWeight: "bold",
                marginTop: 12,
                display: "block",
                marginBottom: 8,
              }}
            >
              Version:
            </label>
            <div className="ef-version-row">
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {["New", "R1", "Checking"].map((v) => (
                  <label
                    key={v}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="radio"
                      name="version"
                      value={v}
                      checked={form.version === v}
                      onChange={(e) => handleVersionChange(e.target.value)}
                    />
                    {v}
                  </label>
                ))}
              </div>

              <input
                ref={checklistInputRef}
                type="file"
                onChange={handleChecklistUpload}
                style={{ display: "none" }}
              />
              <div className="cpm-btn-group">
                <button
                  type="button"
                  className="ef-checklist-btn"
                  onClick={() => checklistInputRef.current?.click()}
                >
                  Upload Checklist
                </button>
                <button
                  type="button"
                  className="ef-checklist-btn"
                  onClick={() => setShowChecklistModal(true)}
                >
                  Checklists (Fill / Assign)
                </button>
              </div>
            </div>

            {checklistError && (
              <div className="ef-checklist-error">{checklistError}</div>
            )}

            {attachChecklist && (
              <div className="ef-checklist-nopreview">
                📋 Checklist "{attachChecklist.templateTitle}" will be attached
                to this entry when you submit.
              </div>
            )}

            {checklistFile && (
              <div className="ef-checklist-panel">
                <div className="ef-checklist-header">
                  <div className="ef-checklist-title">
                    Checklist: {checklistFile.name} (
                    {formatFileSize(checklistFile.size)})
                  </div>
                  <div className="ef-checklist-actions">
                    <button
                      type="button"
                      className="ef-checklist-download-btn"
                      onClick={handleChecklistDownload}
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="ef-checklist-clear-btn"
                      onClick={handleChecklistRemove}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {checklistKind === "table" && checklistRows.length > 0 && (
                  <div className="ef-checklist-scroll">
                    <table className="ef-checklist-table">
                      <thead>
                        <tr>
                          {checklistRows[0].map((h, i) => (
                            <th key={i}>{String(h)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {checklistRows.slice(1).map((row, r) => (
                          <tr key={r}>
                            {checklistRows[0].map((_, c) => (
                              <td key={c}>{String(row[c] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {checklistKind === "image" && checklistPreviewUrl && (
                  <div className="ef-checklist-image-wrap">
                    <img
                      src={checklistPreviewUrl}
                      alt={checklistFile.name}
                      className="ef-checklist-image"
                    />
                  </div>
                )}

                {checklistKind === "pdf" && checklistPreviewUrl && (
                  <iframe
                    src={checklistPreviewUrl}
                    title={checklistFile.name}
                    className="ef-checklist-pdf"
                  />
                )}

                {checklistKind === "other" && (
                  <div className="ef-checklist-nopreview">
                    Preview is not available for this file type. Use Download to
                    open it.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Remark */}
          <div
            style={{
              border: "1px solid #ccc",
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <label style={{ minWidth: 80, fontWeight: "bold", fontSize: 14 }}>
                Remark:
              </label>
              <input
                type="text"
                name="remark"
                value={form.remark || ""}
                onChange={handleChange}
                style={{
                  flex: 1,
                  minWidth: 220,
                  padding: "8px 10px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 14,
                }}
              />
            </div>
          </div>

          {/* Time Entry */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div
              style={{
                border: "2px solid #a9a9a9",
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                position: "relative",
              }}
            >
              <strong
                style={{
                  position: "absolute",
                  top: -10,
                  left: 12,
                  background: "#fff",
                  padding: "0 4px",
                  fontSize: 14,
                }}
              >
                Time Spent:
              </strong>

              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1 1 260px", minWidth: 240 }}>
                  <label>Date:</label>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    style={{
                      width: "95%",
                      padding: "8px 10px",
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      color: "#525252",
                      marginBottom: 12,
                    }}
                  />

                  <label>Start Time:</label>
                  <TimePicker
                    value={
                      form.startTime
                        ? dayjs(`2023-01-01T${form.startTime}`)
                        : null
                    }
                    onChange={(val) =>
                      setForm((prev) => ({
                        ...prev,
                        startTime: val ? dayjs(val).format("HH:mm") : "",
                      }))
                    }
                    ampm
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        style={{ marginBottom: 12, marginTop: 4 }}
                      />
                    )}
                  />
                  <br />

                  <label>End Time:</label>
                  <TimePicker
                    value={
                      form.endTime ? dayjs(`2023-01-01T${form.endTime}`) : null
                    }
                    onChange={(val) =>
                      setForm((prev) => ({
                        ...prev,
                        endTime: val ? dayjs(val).format("HH:mm") : "",
                      }))
                    }
                    ampm
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        style={{ marginTop: 4 }}
                      />
                    )}
                  />
                </div>

                <div
                  style={{
                    flex: "1 1 240px",
                    minWidth: 240,
                    border: "1px solid lightgray",
                    borderRadius: 4,
                    padding: 12,
                    minHeight: 120,
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontWeight: "bold",
                      fontSize: 18,
                    }}
                  >
                    Total spent time:
                  </div>
                  <div style={{ fontSize: 20, color: "#343A76" }}>
                    {actualHours.minutes === 0
                      ? "0 min"
                      : actualHours.minutes < 60
                        ? `${actualHours.minutes} min`
                        : `${Math.floor(actualHours.minutes / 60)} hr ${
                            actualHours.minutes % 60
                          } min`}
                  </div>
                </div>
              </div>

              <div style={{ textAlign: "center", marginTop: 12 }}>
                <label style={{ marginRight: 8 }}>Estimate time:</label>
                <input
                  type="text"
                  value={formatEstimatedTime(form.estimatedHours)}
                  readOnly
                  style={{
                    width: 200,
                    maxWidth: "100%",
                    padding: "8px 10px",
                    border: "1px solid lightgray",
                    borderRadius: 4,
                    textAlign: "center",
                  }}
                />
              </div>
            </div>
          </LocalizationProvider>

          <div style={{ textAlign: "right", marginTop: 12 }}>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                background: submitting ? "#6b7280" : "#343A76",
                color: "white",
                padding: "10px 20px",
                borderRadius: 6,
                border: "none",
                fontWeight: "bold",
                width: "100%",
                maxWidth: 260,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Saving..." : editLogId ? "Update" : "Submit"}
            </button>
          </div>
        </div>

        {/* RIGHT: Tasks and Project summary */}
        <div
          style={{
            flex: "1 1 420px",
            minWidth: 320,
            maxWidth: 520,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div className="card">
            <h3 style={{ marginTop: 0, color: "#b91c1c" }}>
              🔥 My Pending Tasks
            </h3>

            {completedBanner && (
              <div
                style={{
                  background: "#e6ffed",
                  border: "1px solid #34c759",
                  color: "#064d1f",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginBottom: 10,
                }}
              >
                ✅ <strong>Task completed:</strong>{" "}
                {completedBanner.projectName}
              </div>
            )}

            {myPendingTasks.length === 0 ? (
              <div style={{ color: "#666", padding: 8 }}>No pending tasks.</div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {myPendingTasks.map((t) => {
                  const docs = Array.isArray(t.drawingDocuments)
                    ? t.drawingDocuments
                    : [];
                  const remark = t.remark || t.remarks || "";
                  const due = t.dueDate ? new Date(t.dueDate) : null;
                  const est = t.estimatedHours || "—";
                  const createdAt = t.createdAt ? new Date(t.createdAt) : null;

                  const chip = {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    background: "#fff",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    padding: "6px 12px",
                    borderRadius: 999,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  };
                  const sectionBox = {
                    background: "#fff",
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: 10,
                  };
                  const docPill = { ...chip, background: "#fff" };

                  const timeHM = (d) =>
                    d
                      ? d.toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";

                  return (
                    <div
                      key={t._id}
                      style={{
                        borderRadius: 14,
                        padding: 12,
                        background: "#ffffff",
                        boxShadow:
                          "0 1px 2px rgba(0,0,0,0.04), 0 6px 20px rgba(239,68,68,0.08)",
                        border: "1px solid #ef4444",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          borderRadius: 12,
                          border: "2px solid #fca5a5",
                          padding: 12,
                          background: "#ffffff",
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#991b1b",
                          }}
                        >
                          {t.projectName}
                        </div>
                        <div
                          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                        >
                          <span style={chip}>🏷️ {t.typeofProject || "—"}</span>
                          <span style={chip}>🧰 {t.workType || "—"}</span>
                        </div>

                        <div
                          style={{
                            ...sectionBox,
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 700,
                              color: "#111827",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            🖇️ <span>Drawings & Documents</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {docs.length ? (
                              docs.map((doc, i) => (
                                <span key={i} style={docPill}>
                                  📄 {doc}
                                </span>
                              ))
                            ) : (
                              <span
                                style={{
                                  ...docPill,
                                  color: "#6b7280",
                                  borderColor: "#f3f4f6",
                                }}
                              >
                                📄 No Docs
                              </span>
                            )}
                          </div>
                        </div>

                        {remark ? (
                          <div
                            style={{
                              ...sectionBox,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 14,
                            }}
                          >
                            📝{" "}
                            <strong style={{ color: "#374151" }}>
                              Remark:
                            </strong>{" "}
                            <span style={{ fontWeight: 500, color: "#111827" }}>
                              {remark}
                            </span>
                          </div>
                        ) : null}

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            color: "#0f172a",
                            fontSize: 14,
                          }}
                        >
                          <span style={chip}>⏱ Est: {est} h</span>
                          <span style={chip}>
                            📅Due date:{" "}
                            {due
                              ? due.toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "No Due"}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            paddingTop: 8,
                            borderTop: "1px dashed #fca5a5",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontWeight: 600,
                              color: "#991b1b",
                            }}
                          >
                            <input
                              type="radio"
                              name="selectedTask"
                              checked={selectedTaskId === t._id}
                              onChange={() => {
                                setSelectedTaskId(t._id);
                                setProjectTypeFilter("All");
                                const versionToUse =
                                  (form.version && form.version.trim()) ||
                                  "New";
                                setForm((prev) => ({
                                  ...prev,
                                  projectName:
                                    t.projectName || prev.projectName,
                                  typeofProject:
                                    t.typeofProject || prev.typeofProject,
                                  workType: t.workType || prev.workType,
                                  drawingDocument: docs,
                                  version: versionToUse,
                                }));
                              }}
                            />
                            Select Task
                          </label>

                          <div
                            style={{
                              marginLeft: "auto",
                              fontSize: 12,
                              color: "#6b7280",
                            }}
                          >
                            {createdAt ? timeHM(createdAt) : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Assigned checklists (pending + filled but not attached + to check) */}
          <ChecklistTasksCard
            user={user}
            refreshKey={checklistRefreshKey}
            selectedId={attachChecklist?._id || null}
            onSelect={handleSelectChecklist}
          />

          {/* Project-wise Summary */}
          <div className="card">
            <h4
              style={{
                fontSize: 16,
                marginBottom: 10,
                color: "#343A76",
                textAlign: "center",
              }}
            >
              Project-wise Summary
            </h4>
            <div className="table-wrap">
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                  minWidth: 520,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        padding: 8,
                        borderBottom: "2px solid #343A76",
                        textAlign: "left",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      Project
                    </th>
                    <th
                      style={{
                        padding: 8,
                        borderBottom: "2px solid #343A76",
                        textAlign: "center",
                      }}
                    >
                      Est.
                    </th>
                    <th
                      style={{
                        padding: 8,
                        borderBottom: "2px solid #343A76",
                        textAlign: "center",
                      }}
                    >
                      Act.
                    </th>
                    <th
                      style={{
                        padding: 8,
                        borderBottom: "2px solid #343A76",
                        textAlign: "center",
                      }}
                    >
                      Diff
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(projectHours).map(([project, data]) => {
                    const diff = data.estimated - data.actual;
                    return (
                      <tr key={project}>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #ccc",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {project}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #ccc",
                            textAlign: "center",
                          }}
                        >
                          {data.estimated.toFixed(2)}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #ccc",
                            textAlign: "center",
                          }}
                        >
                          {data.actual.toFixed(2)}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #ccc",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: diff < 0 ? "red" : "green",
                          }}
                        >
                          {diff.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#eef2ff",
                      borderTop: "2px solid #343A76",
                    }}
                  >
                    <td style={{ padding: 8 }}>Total</td>
                    <td style={{ padding: 8, textAlign: "center" }}>
                      {Object.values(projectHours)
                        .reduce((s, p) => s + p.estimated, 0)
                        .toFixed(2)}
                    </td>
                    <td style={{ padding: 8, textAlign: "center" }}>
                      {Object.values(projectHours)
                        .reduce((s, p) => s + p.actual, 0)
                        .toFixed(2)}
                    </td>
                    <td style={{ padding: 8, textAlign: "center" }}>
                      {Object.values(projectHours)
                        .reduce((s, p) => s + (p.estimated - p.actual), 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Logs header */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
          marginTop: 16,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <h4 style={{ fontSize: 18, color: "#343A76", margin: 0 }}>
          Logs for {selectedMonth ? getMonthLabel(selectedMonth) : "—"}
        </h4>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            width: 220,
            maxWidth: "100%",
            padding: "8px 12px",
            border: "1px solid #ccc",
            borderRadius: 6,
            backgroundColor: "#eef2ff",
            fontSize: 15,
          }}
        >
          {uniqueMonthYears.map((val) => (
            <option key={val} value={val}>
              {getMonthLabel(val)}
            </option>
          ))}
        </select>
      </div>

      {/* Logs table */}
      <div
        className="table-wrap"
        style={{
          width: "100%",
          maxHeight: "75vh",
          overflowY: "auto",
          overflowX: "auto",
          position: "relative",
          border: "1px solid #ddd",
          borderRadius: 8,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          paddingBottom: "50px",
        }}
      >
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Project</th>
              <th style={styles.th}>Drawing</th>
              <th style={styles.th}>Drawing & Document</th>
              <th style={styles.th}>Remark</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Start</th>
              <th style={styles.th}>End</th>
              <th style={styles.th}>Estimated</th>
              <th style={styles.th}>Actual</th>
              <th style={styles.th}>Diff</th>
              <th style={styles.th}>Checklist</th>
              <th style={styles.th}>Edit</th>
              <th style={styles.th}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {logsThisMonth.map((log) => {
              const est = parseFloat(log.estimatedHours || 0);
              const act = parseFloat(log.actualHours || 0);
              const diff = est - act;
              const attachedChecklistId = attachedByLog[String(log._id)];
              const hasUploadedFile = Boolean(log.checklistFile?.originalName);

              return (
                <tr key={log._id}>
                  <td style={styles.td}>{log.projectName}</td>
                  <td style={styles.td}>{log.workType}</td>
                  <td style={{ ...styles.td, whiteSpace: "normal" }}>
                    {Array.isArray(log.drawingDocuments)
                      ? log.drawingDocuments.join(" | ")
                      : log.drawingDocument}
                  </td>
                  <td style={styles.td}>{log.remark}</td>
                  <td style={styles.td}>
                    {new Date(log.date).toLocaleDateString("en-GB")}
                  </td>
                  <td style={styles.td}>{log.startTime}</td>
                  <td style={styles.td}>{log.endTime}</td>
                  <td style={styles.td}>{formatEstimatedTime(est)}</td>
                  <td style={styles.td}>{formatEstimatedTime(act)}</td>
                  <td
                    style={{
                      ...styles.td,
                      fontWeight: "bold",
                      color: diff < 0 ? "red" : "green",
                    }}
                  >
                    {diff.toFixed(2)}
                  </td>
                  <td style={styles.td}>
                    {hasUploadedFile || attachedChecklistId ? (
                      <div className="cpm-btn-group">
                        {hasUploadedFile && (
                          <button
                            type="button"
                            className="ef-log-checklist-btn"
                            onClick={() => handleLogChecklistDownload(log)}
                          >
                            📎 Download
                          </button>
                        )}
                        {attachedChecklistId && (
                          <button
                            type="button"
                            className="ef-log-checklist-btn"
                            onClick={() =>
                              handleViewAttachedChecklist(attachedChecklistId)
                            }
                          >
                            📋 View
                          </button>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleEdit(log)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                    >
                      ✏️
                    </button>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleDelete(log._id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>

          {/* Sticky footer total */}
          <tfoot>
            <tr
              style={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "#e6f0ff",
                fontWeight: "bold",
                borderTop: "2px solid #343A76",
                boxShadow: "0 -2px 4px rgba(0,0,0,0.08)",
                zIndex: 2,
              }}
            >
              <td
                colSpan="4"
                style={{
                  padding: 10,
                  color: "#2b3a67",
                  fontSize: 16,
                  textAlign: "left",
                }}
              >
                🧮 <strong>Total:</strong>
              </td>
              <td colSpan="3"></td>
              <td
                style={{
                  padding: 10,
                  fontSize: 16,
                  color: "#2b3a67",
                  textAlign: "center",
                }}
              >
                {totalEstimated.toFixed(2)}
              </td>
              <td
                style={{
                  padding: 10,
                  fontSize: 16,
                  color: "#2b3a67",
                  textAlign: "center",
                }}
              >
                {totalActual.toFixed(2)}
              </td>
              <td
                style={{
                  padding: 10,
                  fontSize: 16,
                  color: totalEstimated - totalActual < 0 ? "red" : "green",
                  textAlign: "center",
                }}
              >
                {(totalEstimated - totalActual).toFixed(2)}
              </td>
              <td colSpan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Checklist fill / assign popup */}
      {showChecklistModal && (
        <ChecklistPanelModal
          user={user}
          onClose={() => {
            setShowChecklistModal(false);
            setChecklistRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* Read-only view of a checklist attached to a log (comments allowed) */}
      {viewChecklistConfig && (
        <ChecklistFillModal
          config={viewChecklistConfig}
          onClose={() => setViewChecklistConfig(null)}
        />
      )}
    </ThemeProvider>
  );
};

/* ---------------------- styles & export ---------------------- */
const styles = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
    backgroundColor: "#fff",
    minWidth: 900,
  },
  th: {
    backgroundColor: "#343A76",
    color: "#fff",
    padding: "8px 10px",
    textAlign: "left",
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #e0e0e0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default EmployeeForm;
